// Shared discount/pricing calculations, ported from sge_infantil_web's
// student create/edit pages (handleSelectClass + handleSelectDiscount + the
// scholarship blocks in create/index.js, roughly lines 447-524 and
// 2093-2620). Used by both CadastrarEstudanteLayer and EditarEstudanteLayer
// so the two stay in sync.

export const DISCOUNT_OPTIONS = [
  0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5, 0.6, 0.7, 0.75, 0.8, 0.9, 1.0,
];

// Base values (before any discount) computed from the selected class/level
// (frequency) and the chosen extra-curricular activities. Faithfully mirrors
// handleSelectClass/getActivities in the original: `monthlyPayment` only
// ever reflects the sum of the selected activities' recurring amounts — the
// frequency's own base monthlyPayment is never folded into it. That looks
// like a quirk of the original app, but it's what the backend/original UI
// already does today, so it's kept as-is rather than "fixed" here.
export function computeBaseTotals({ freq, isNew, selectedActivities }) {
  if (!freq) {
    return { registrationValue: 0, monthlyPayment: 0, total: 0 };
  }
  const registrationBase = parseFloat(
    (isNew ? freq.registrationValue : freq.recurigRegistrationValue) || 0,
  );
  const extraRecurring = (selectedActivities || []).reduce(
    (acc, item) => acc + parseFloat(item.recuringAmount || 0),
    0,
  );
  const extraRegistration = (selectedActivities || []).reduce(
    (acc, item) => acc + parseFloat(item.registrationAmount || 0),
    0,
  );
  const totalRecurringAmount = extraRecurring;
  const totalSubscriptionAmount = extraRegistration + registrationBase;
  const total = totalSubscriptionAmount + totalRecurringAmount;
  return {
    registrationValue: total,
    monthlyPayment: totalRecurringAmount,
    total: totalSubscriptionAmount,
  };
}

// Applies the scholarship/discount configuration on top of the "original"
// (pre-discount) values, mirroring handleSelectDiscount plus the inline
// full-scholarship / "applies to both" handlers in create/index.js.
export function applyDiscount({
  scholarshipType,
  discountType,
  discountAppliesTo,
  discount,
  registrationDiscount,
  monthlyDiscount,
  exemptRegistration,
  originalRegistrationValue,
  originalMonthlyPayment,
}) {
  let registrationValue = originalRegistrationValue;
  let monthlyPayment = originalMonthlyPayment;
  let total = originalRegistrationValue;

  if (scholarshipType === "full") {
    monthlyPayment = 0;
    if (exemptRegistration) {
      registrationValue = 0;
      total = 0;
    } else {
      const pct = parseFloat(registrationDiscount) || 0;
      registrationValue = originalRegistrationValue * (1 - pct);
      total = registrationValue;
    }
  } else if (scholarshipType === "partial" || scholarshipType === "discount") {
    if (discountAppliesTo === "both") {
      const regD = parseFloat(registrationDiscount) || 0;
      const monD = parseFloat(monthlyDiscount) || 0;
      if (discountType === "percentage") {
        registrationValue = originalRegistrationValue * (1 - regD);
        monthlyPayment = originalMonthlyPayment * (1 - monD);
      } else {
        registrationValue = originalRegistrationValue - regD;
        monthlyPayment = originalMonthlyPayment - monD;
      }
      total = registrationValue;
    } else if (discountAppliesTo === "payment") {
      const d = parseFloat(discount) || 0;
      monthlyPayment =
        discountType === "percentage"
          ? originalMonthlyPayment * (1 - d)
          : originalMonthlyPayment - d;
      total = originalRegistrationValue;
    } else {
      // "registration" (default)
      const d = parseFloat(discount) || 0;
      registrationValue =
        discountType === "percentage"
          ? originalRegistrationValue * (1 - d)
          : originalRegistrationValue - d;
      total = registrationValue;
    }
  }

  return {
    registrationValue: Math.max(0, registrationValue),
    monthlyPayment: Math.max(0, monthlyPayment),
    total: Math.max(0, total),
  };
}

// Converts the UI's discount state into the numeric fields the backend
// expects (percentages sent as 0-100, the "both" fields always sent as MZN
// amounts regardless of discountType) — mirrors handleStudentCreationCallBack
// in both create/index.js and edit/index.js exactly.
export function buildDiscountPayloadFields({
  discountType,
  discount,
  registrationDiscount,
  monthlyDiscount,
  freqRegistrationValue,
  freqMonthlyPayment,
}) {
  const discountToSend =
    discountType === "percentage" && discount
      ? parseFloat(discount) * 100
      : parseFloat(discount) || 0;

  const registrationDiscountToSend = registrationDiscount
    ? discountType === "percentage"
      ? freqRegistrationValue * parseFloat(registrationDiscount)
      : parseFloat(registrationDiscount)
    : 0;

  const monthlyDiscountToSend = monthlyDiscount
    ? discountType === "percentage"
      ? freqMonthlyPayment * parseFloat(monthlyDiscount)
      : parseFloat(monthlyDiscount)
    : 0;

  return { discountToSend, registrationDiscountToSend, monthlyDiscountToSend };
}
